import React, { useState, useCallback } from 'react';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { MonthSelectorStyles } from './MonthSelector.styles';
import { useDateFns } from '~/Hooks';

interface IMonthSelectorProps {
  startDate?: Date;
  endDate?: Date;
  onDateChange?: Function;
}

export const MonthSelector = ({
  startDate,
  endDate,
  onDateChange = () => {},
}: IMonthSelectorProps) => {
  const { classes, cx } = MonthSelectorStyles();
  const { formatDate } = useDateFns();
  const [startYear, setStartYear] = useState((startDate || new Date()).getFullYear());
  const [endYear, setEndYear] = useState((endDate || new Date()).getFullYear() + (endDate ? 0 : 1));
  const getMonth = useCallback(
    (date?: Date) => (date ? date.getMonth() : new Date().getMonth()),
    [],
  );
  const currentMonth = getMonth(new Date());
  const currentYear = new Date().getFullYear();

  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const changeYear = useCallback(
    ({
      plus,
      isStart,
      startYear,
      endYear,
    }: {
      plus: boolean;
      isStart: boolean;
      startYear: number;
      endYear: number;
    }) => {
      if (isStart) {
        const target = plus ? startYear + 1 : startYear - 1;
        if (target === endYear) {
          return;
        }
        setStartYear(target);
      } else {
        const target = plus ? endYear + 1 : endYear - 1;
        if (target === startYear || target > new Date().getFullYear()) {
          return;
        }
        setEndYear(target);
      }
    },
    [],
  );

  return (
    <div className={classes.dateContainer}>
      <div className={classes.monthViewContainer}>
        <div className={classes.headerControl}>
          <span className={classes.startYear}>{startYear}</span>
          <div className={classes.controls}>
            <div
              data-testid="prev-year-back"
              className={classes.controlsBtn}
              onClick={() => changeYear({ plus: false, isStart: true, startYear, endYear })}
            >
              <KeyboardArrowLeft />
            </div>
            <div
              className={classes.controlsBtn}
              onClick={() => changeYear({ plus: true, isStart: true, startYear, endYear })}
            >
              <KeyboardArrowRight />
            </div>
          </div>
        </div>
        <div className={classes.monthViewWrapper}>
          {months.map((month: number) => {
            const isStart = month === getMonth(startDate) && startDate?.getFullYear() === startYear;
            const isEnd = month === getMonth(endDate) && endDate?.getFullYear() === startYear;
            const isActive = isStart || isEnd;
            return (
              <p
                data-testid={`${month}-${startYear}`}
                key={`${month}-${startYear}`}
                className={cx(classes.month, {
                  ['active']: isActive,
                  ['currentMonth']: currentYear === startYear && month === currentMonth,
                  ['grayedOut']:
                    startDate &&
                    endDate &&
                    new Date(startYear, month, 1) > startDate &&
                    new Date(startYear, month, 1) < endDate,
                  ['start']: isStart,
                  ['end']: isEnd || (!!isStart && !endDate),
                })}
                onClick={() => onDateChange(new Date(startYear, month, 1))}
              >
                {formatDate(new Date(startYear, month, 1).toDateString(), 'LLL')}
              </p>
            );
          })}
        </div>
      </div>
      <div className={classes.monthViewContainer}>
        <div className={classes.headerControl}>
          <span className={classes.startYear}>{endYear}</span>
          <div className={classes.controls}>
            <div
              className={classes.controlsBtn}
              onClick={() => changeYear({ plus: false, isStart: false, startYear, endYear })}
            >
              <KeyboardArrowLeft />
            </div>
            <div
              className={classes.controlsBtn}
              onClick={() => changeYear({ plus: true, isStart: false, startYear, endYear })}
            >
              <KeyboardArrowRight />
            </div>
          </div>
        </div>
        <div className={classes.monthViewWrapper}>
          {months.map((month: number) => {
            const isStart = month === getMonth(startDate) && startDate?.getFullYear() === endYear;
            const isEnd = month === getMonth(endDate) && endDate?.getFullYear() === endYear;
            const isActive = isStart || isEnd;
            return (
              <p
                data-testid={`${month}-${endYear}`}
                key={`${month}-${endYear}`}
                className={cx(classes.month, {
                  ['active']: isActive,
                  ['currentMonth']: currentYear === endYear && month === currentMonth,
                  ['disabled']: currentYear === endYear && month > currentMonth,
                  ['grayedOut']:
                    startDate &&
                    endDate &&
                    new Date(endYear, month, 1) > startDate &&
                    new Date(endYear, month + 1, 0) < endDate,
                  ['start']: isStart,
                  ['end']: isEnd || (!!isStart && !endDate),
                })}
                onClick={() => onDateChange(new Date(endYear, month, 1))}
              >
                {formatDate(new Date(endYear, month, 1).toDateString(), 'LLL')}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
};
